import { useFlags } from '@atlaskit/flag';
import { AddSubLocationFormFields } from 'core/config/form-fields';
import { useDialog } from 'core/providers/DialogProvider';
import { showErrorFlag, showSuccessFlag } from 'core/utilities/flags-helper';
import { useEffect, useState } from 'react';
import { IButton } from 'shared/components/buttons/button-interface';
import SharedDialogBase from 'shared/components/dialog-base/dialog-base';
import SharedForm from 'shared/components/form/form';
import { SubLocationsApiService } from 'core/api';
import { useAuthState } from 'core/providers/AuthProvider';
import { ISubLocationDao, ISubLocationDto } from 'core/api/types/sub-locations.interface';
import { ILocationDao } from 'core/api/types';
import { ISharedFormField } from 'shared/components/form/form-interface';

import { IAddEditSubLocationFormOutput } from '../types';
import { useSharedData } from 'core/providers/SharedDataProvider';

const AddSubLocationDialog = ({
  location,
  subLocation,
}: {
  location?: ILocationDao;
  subLocation?: ISubLocationDao;
}) => {
  const { userData } = useAuthState();
  const flags = useFlags();
  const dialog = useDialog();
  const { processTypes } = useSharedData();

  const [formFields, setFormFields] = useState<ISharedFormField[]>(AddSubLocationFormFields);
  const [formSubmitting, setFormSubmitting] = useState(false);

  const addSubLocation = async (data: IAddEditSubLocationFormOutput) => {
    setFormSubmitting(true);

    const payload: ISubLocationDto = {
      locationId: location?.uid || subLocation?.locationId || '',
      active: data.active,
      name: data.name,
      notes: data.notes,
    };

    try {
      if (subLocation) {
        await SubLocationsApiService.update(subLocation.uid, payload, {
          fullName: userData?.fullName ?? '',
          uid: userData?.uid ?? '',
        });
      } else {
        await SubLocationsApiService.set(payload, {
          fullName: userData?.fullName ?? '',
          uid: userData?.uid ?? '',
        });
      }

      showSuccessFlag(
        `Sub location ${subLocation ? 'updated' : 'created'}`,
        `The sub location has been ${subLocation ? 'updated' : 'created'} successfully`,
        flags
      );
      dialog?.closeDialog();
    } catch (error) {
      showErrorFlag(
        `Sub location ${subLocation ? 'update' : 'creation'} failed`,
        `Unable to ${subLocation ? 'update' : 'create'} a sub location, please try again.`,
        flags
      );
      setFormSubmitting(false);
    }
  };

  const cancelButton: IButton = {
    onClick: () => dialog?.closeDialog(),
    label: 'Cancel',
    appearance: 'subtle',
    type: 'button',
  };

  const getDefaultValue = (subLocation: ISubLocationDao, key: string) => {
    switch (key) {
      default:
        return subLocation[key as keyof ISubLocationDao];
    }
  };

  useEffect(() => {
    const fields = AddSubLocationFormFields.map((field) => {
      return {
        ...field,
        defaultValue: subLocation ? getDefaultValue(subLocation, field.key) : field.defaultValue,
      };
    });

    setFormFields(fields as ISharedFormField[]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processTypes, subLocation]);

  const customContent = () => {
    return (
      <SharedForm
        className='p-4 overflow-y-auto'
        onSubmit={addSubLocation}
        fields={formFields as ISharedFormField[]}
        buttonLabel='Submit'
        loading={formSubmitting}
        cancelButton={cancelButton}
      />
    );
  };

  return (
    <SharedDialogBase
      title={subLocation ? 'Update sub location' : 'Add sub location'}
      customContentTemplate={customContent()}
      showButtons={false}
    />
  );
};

export default AddSubLocationDialog;
